
import React from 'react';
import { Button } from '@/components/ui/button';
import { Gift, UserRound, LogIn } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface WelcomeScreenProps {
  onRoleSelect: (role: 'giver' | 'recipient') => void;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onRoleSelect }) => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  
  const handleLoginClick = () => {
    navigate('/auth');
  };
  
  // Show a loading state while checking authentication
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center max-w-md mx-auto space-y-8 py-8">
        <div className="text-center">
          <div className="w-24 h-24 rounded-full bg-givgo-purple/20 flex items-center justify-center mx-auto animate-pulse">
            <div className="w-16 h-16 rounded-full bg-givgo-purple/40"></div>
          </div>
          <h1 className="text-4xl font-bold mt-6 text-givgo-purple">Loading GivGo...</h1>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center justify-center max-w-md mx-auto space-y-8 py-8 animate-fade-in">
      <div className="text-center">
        <div className="inline-block p-6 rounded-full bg-givgo-light mb-6 mx-auto">
          <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center overflow-hidden mx-auto">
            <img 
              src="/lovable-uploads/00c921fe-bb3a-4eb5-ad50-76a967cd91c7.png" 
              alt="GivGo Logo" 
              className="h-20 w-20 object-cover rounded-full"
            />
          </div>
        </div>
        <h1 className="text-4xl font-bold mb-2 text-givgo-purple">Welcome to GivGo</h1>
        <p className="text-lg italic text-givgo-purple mb-2">When a "like" is not enough</p>
        <p className="text-lg text-muted-foreground mb-8">
          Have a ton of fun sending and receiving personalized micro-gifts to your friends and family
        </p>
      </div>
      
      {!user ? (
        // Non-authenticated user view
        <div className="w-full space-y-6">
          <Button 
            onClick={handleLoginClick} 
            className="w-full bg-givgo-purple hover:bg-givgo-purple/90 text-white"
          >
            <LogIn className="mr-2 h-4 w-4" />
            Login or Create Account
          </Button>
          
          <div className="text-center text-sm text-muted-foreground">
            <p>You need to login or create an account to send and receive micro-gifts.</p>
          </div>
        </div>
      ) : (
        // Authenticated user view
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
          <Card className="p-6 hover:border-givgo-purple transition-colors cursor-pointer" onClick={() => onRoleSelect('giver')}>
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-givgo-light flex items-center justify-center text-givgo-purple">
                <Gift size={28} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">I want to send a micro-gift</h2>
                <p className="text-sm text-muted-foreground mt-1">Brighten someone's day with a micro-gift</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 hover:border-givgo-purple transition-colors cursor-pointer" onClick={() => onRoleSelect('recipient')}>
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-givgo-light flex items-center justify-center text-givgo-purple">
                <UserRound size={28} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">I received a micro-gift</h2>
                <p className="text-sm text-muted-foreground mt-1">View and redeem your micro-gifts</p>
              </div>
            </div>
          </Card>
        </div>
      )}
      
      <div className="text-center text-sm text-muted-foreground mt-4">
        <p>GivGo makes it so easy to send/receive personalized micro-gifts. USE them, EXCHANGE them, RE-GIFT them, or even CONVERT them to cash...</p>
        <p className="font-bold">YOU DECIDE!</p>
      </div>
    </div>
  );
};

export default WelcomeScreen;
